import { useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import type { BillElementOut } from "../../../../api/bills";
import { getUsers, type UserListItemOut } from "../../../../api/auth";
import { initialsOf } from "./waiterInitials";

const NO_USERS: UserListItemOut[] = [];

/** Кой е маркирал поръчката — до часа в разделителя ѝ. */
export interface BatchWaiterLabel {
  /** Цялото име, а в тесния вид (телефон, панелът до схемата) — инициалите
   *  с точки („М.П."): до часа така се четат като име, не като съкращение. */
  label: string;
  /** Цялото име — за подсказката върху инициалите. */
  fullName: string;
}

/** „Мария Петрова" → „М.П.", „Иван" → „И.". */
export const dottedInitialsOf = (name: string) =>
  Array.from(initialsOf(name))
    .map((letter) => `${letter}.`)
    .join("");

/** Колко различни души са маркирали показаните поръчки (без празните). */
export const orderingWaiterCount = (elements: BillElementOut[]) =>
  new Set(elements.filter((element) => element.items.length > 0).map((element) => element.waiter_id)).size;

/**
 * Името на маркиралия за всяка поръчка (`bill_elements.waiter_id` — влезлият
 * при „Поръчай") — САМО ако по сметката са маркирали поне двама; иначе null
 * навсякъде. `short` — инициали с точки вместо цялото име.
 *
 * Имената са от кеша на `useUsers()` (`["users", "all", false, "none"]`, като
 * полето „Сервитьор" в схемата): `GET /user/users` е в одита, затова тук без
 * четене при всяко монтиране — само ако кешът е по-стар от 5 минути. Непознат
 * потребител — без име.
 */
export const useBatchWaiterLabels = (elements: BillElementOut[], short: boolean) => {
  const severalWaiters = orderingWaiterCount(elements) > 1;
  const { data: users = NO_USERS } = useQuery({
    queryKey: ["users", "all", false, "none"],
    queryFn: () => getUsers(),
    enabled: severalWaiters,
    staleTime: 5 * 60_000,
  });
  const namesById = useMemo(
    () => new Map(users.map((user) => [user.id, user.names || user.username])),
    [users],
  );
  return useCallback(
    (element: BillElementOut): BatchWaiterLabel | null => {
      if (!severalWaiters) return null;
      const fullName = namesById.get(element.waiter_id);
      if (!fullName) return null;
      return { label: short ? dottedInitialsOf(fullName) : fullName, fullName };
    },
    [severalWaiters, namesById, short],
  );
};
